import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { GraduationCap, LogOut, BookOpen, Clock3, Award } from "lucide-react";
import { SITE } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [checked, setChecked] = useState(!!location.state?.user);

  useEffect(() => {
    if (location.state?.user) return;
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(res.data);
      } catch {
        navigate("/");
      } finally {
        setChecked(true);
      }
    };
    checkAuth();
  }, []);

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    window.location.href = "/";
  };

  if (!checked || !user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="font-mono2 text-sm text-slate-400">Checking your session...</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>My Dashboard | LearnWithTAC</title>
      </Helmet>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20" data-testid="student-dashboard">
        <div className="rounded-2xl border border-sky-500/25 bg-[#0B1226] p-7 sm:p-9 flex flex-col sm:flex-row items-start sm:items-center gap-6">
          {user.picture ? (
            <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-2xl border border-sky-500/30 object-cover" />
          ) : (
            <span className="w-16 h-16 rounded-2xl bg-blue-600/25 border border-sky-500/30 flex items-center justify-center font-display text-2xl font-bold text-sky-300">
              {user.name?.[0] || "S"}
            </span>
          )}
          <div className="flex-1">
            <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-orange-500 font-semibold">Welcome back</p>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-white mt-1">{user.name}</h1>
            <p className="text-sm text-slate-400 mt-0.5">{user.email}</p>
          </div>
          <button
            data-testid="dashboard-logout-btn"
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-xl border border-sky-500/40 text-sky-300 hover:text-white hover:bg-sky-500/10 font-display font-semibold text-sm px-5 py-3 transition-colors duration-200"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mt-8">
          {[
            { icon: BookOpen, title: "My Courses", desc: "Your enrolled 1-on-1 courses will appear here once the LMS portal is connected." },
            { icon: Clock3, title: "Upcoming Sessions", desc: "Your scheduled live sessions with your trainer will show up here." },
            { icon: Award, title: "Certificates", desc: "Your completion certificates with unique IDs will be available here." },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="rounded-2xl border border-sky-500/20 bg-[#0B1226] p-6">
              <div className="w-11 h-11 rounded-xl bg-blue-600/20 border border-sky-500/30 flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-sky-400" />
              </div>
              <h3 className="font-display text-base font-bold text-white">{title}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-2xl border border-orange-500/30 bg-gradient-to-r from-orange-500/10 to-transparent p-7 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div className="flex-1">
            <h3 className="font-display text-lg font-bold text-white">Not enrolled yet?</h3>
            <p className="text-sm text-slate-400 mt-1">Browse the catalog and book your free 1-on-1 demo with a working industry engineer.</p>
          </div>
          <Link
            data-testid="dashboard-browse-courses-btn"
            to="/courses"
            className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-display font-bold text-sm px-6 py-3.5 transition-colors duration-200"
          >
            <GraduationCap className="w-4 h-4" /> Browse Courses
          </Link>
        </div>

        <p className="mt-6 text-xs text-slate-600">
          LMS portal access: <a href={SITE.lmsLoginUrl} target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:text-sky-300">{SITE.lmsLoginUrl}</a>
        </p>
      </section>
    </>
  );
}
